import { useContext, useMemo } from 'react'
import { __RouterContext as RouterContext, matchPath } from 'react-router'
import { LMS_LEARNER_PATHS } from '../../view/lms-learner/route-lms'
import { ROLE_ORG, useRoleContext } from '../role.context'
import { useAuthContext } from '../../context/auth.context'

// Whether the signed-in person has any admin navigation in the current
// organization: exactly the NAVIGATION entries sidebar-left.js can show
// (Dashboard, Analytics, Content/Templates, Users, Groups, Self register
// with its feature, Docs in a company organization), or any system admin
// section. Without it the sidebar would be an empty column, so the layout
// drops it and the landing is the learner page (owner feedback 2026-09-27).
//
// ready: the organization's permissions have loaded. Until then `has` is
// true, so an administrator never sees the sidebar flicker away.
export const hasAdminNavigation = ({ claims, orgRoles }) => {
  if (!claims) return false
  if (claims.isSysAdmin || claims.isSysRoot) return true

  const read = key => !!(orgRoles && orgRoles[key] && orgRoles[key].accessRead)

  return (
    read(ROLE_ORG.DASHBOARD) ||
    read(ROLE_ORG.ANALYTIC) ||
    read(ROLE_ORG.NODE) ||
    read(ROLE_ORG.USER) ||
    read(ROLE_ORG.GROUP) ||
    (read(ROLE_ORG.TRADESHOW) && !!claims.featureTradeShow) ||
    (read(ROLE_ORG.DOC) && claims.organizationType === 'company')
  )
}

// The admin sections the sidebar's NAVIGATION (and the system sections)
// lead to. Account pages under /org (settings, password, sessions, SSO, ...)
// are not here: everyone uses those from the account menu.
export const ADMIN_SECTIONS = [
  '/org/dashboard',
  '/org/analytic',
  '/org/node-content',
  '/org/node-template',
  '/org/node-library',
  '/org/node',
  '/org/user',
  '/org/group',
  '/org/tradeshow',
  '/org/self-register',
  '/admin',
  '/root'
]

export const isAdminSection = path => {
  const pathname = String(path || '').split(/[?#]/)[0]
  return ADMIN_SECTIONS.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}-`))
}

// orgRoles by identifier from a role-permission list (the API's rows).
export const orgRolesOf = permissions => {
  const roles = {}
  ;(permissions || []).forEach(permission => permission && (roles[permission.identifier] = permission))
  return roles
}

// The LMS launch window (/lms/launch/:intent, /link) is the learner's page
// whoever opens it: an organization admin gets it in the same bare layout,
// with no admin sidebar (owner dev run 2026-10-30). Read from the router's
// context when there is one, so a caller outside a router still works.
export const useOnLaunchWindow = () => {
  const router = useContext(RouterContext)
  const pathname = router && router.location && router.location.pathname
  return !!pathname && !!matchPath(pathname, { path: LMS_LEARNER_PATHS })
}

export const useAdminNavigation = () => {
  const onLaunchWindow = useOnLaunchWindow()
  const authContext = useAuthContext()
  const roleContext = useRoleContext()

  const claims = authContext.state.jwtClaims
  const ready = !!(roleContext && roleContext.orgLoaded) || !!(claims && (claims.isSysAdmin || claims.isSysRoot))

  return useMemo(
    () => ({ ready, has: !onLaunchWindow && (!ready || hasAdminNavigation({ claims, orgRoles: roleContext && roleContext.orgRoles })) }),
    [ready, claims, roleContext, onLaunchWindow]
  )
}
